import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaBook, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchAPI } from '../utils/api';
// Admin Components
import AdminStats from '../components/dashboard/admin/AdminStats';
import RevenueChart from '../components/dashboard/admin/RevenueChart';
import ActivityChart from '../components/dashboard/admin/ActivityChart';
import RecentTransactions from '../components/dashboard/admin/RecentTransactions';
import QuickActions from '../components/dashboard/admin/QuickActions';
// Student Components
import RecentActivity from '../components/dashboard/RecentActivity';
import ContinueWatching from '../components/dashboard/ContinueWatching';
import RecentPaymentsWidget from '../components/dashboard/RecentPaymentsWidget';

export default function Dashboard() {
    const { currentUser } = useAuth();
    const role = currentUser?.role;
    const [adminStats, setAdminStats] = useState(null);
    const [loadingAdmin, setLoadingAdmin] = useState(false);

    useEffect(() => {
        const loadAdminData = async () => {
            if (role === 'admin') {
                setLoadingAdmin(true);
                try {
                    const data = await fetchAPI('/admin/get_dashboard_stats.php');
                    setAdminStats(data);
                } catch (error) {
                    console.error("Failed to load admin stats", error);
                } finally {
                    setLoadingAdmin(false);
                }
            }
        };
        loadAdminData();
    }, [role]);

    return (
        <div className="relative min-h-screen overflow-hidden rounded-3xl">
            {/* Background Decorations */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-50 via-white to-blue-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

            <div className="relative z-10 space-y-6 p-1">

                {/* Welcome Card */}
                <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Welcome back, <span className="text-blue-600">{currentUser?.name?.split(' ')[0]}</span>!
                    </h1>
                    <p className="text-gray-500 font-medium">
                        {role === 'admin'
                            ? "Here's your center's performance overview"
                            : "Here's an overview of your learning progress and upcoming tasks."}
                    </p>
                </div>

                {/* --- ADMIN VIEW --- */}
                {role === 'admin' && (
                    <>
                        {loadingAdmin ? (
                            <div className="text-center py-12">Loading Admin Dashboard...</div>
                        ) : adminStats ? (
                            <>
                                <AdminStats stats={adminStats.counts} />
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <RevenueChart data={adminStats.revenue_trend} />
                                    <ActivityChart data={adminStats.student_growth} />
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="lg:col-span-2">
                                        <RecentTransactions transactions={adminStats.recent_transactions} />
                                    </div>
                                    <div>
                                        <QuickActions pendingCount={adminStats.counts.pending_payments} />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center text-red-500">Failed to load admin data</div>
                        )}
                    </>
                )}

                {/* --- STUDENT VIEW --- */}
                {role === 'student' && (
                    <>
                        <StudentDashboardView currentUser={currentUser} />

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                            <RecentActivity currentUser={currentUser} />
                            <div className="space-y-6">
                                <ContinueWatching />
                                <RecentPaymentsWidget />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function StudentDashboardView({ currentUser }) {
    const [stats, setStats] = useState({
        enrolled_courses: '-',
        average_grade: '-',
        payment_status: '-',
        profile: { name: '', index_number: '', grade: '', joined_date: '' },
        attendance: { last_attended: '-', total_classes: 0 }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            if (currentUser?.role !== 'student') return;
            try {
                const data = await fetchAPI('/student/get_dashboard_stats.php');
                if (data) {
                    setStats(data);
                }
            } catch (error) {
                console.error("Failed to fetch student stats:", error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, [currentUser]);

    const getPaymentColor = (status) => {
        switch (status) {
            case 'Paid': return 'text-emerald-600 bg-emerald-50 border-emerald-100';
            case 'Pending': return 'text-amber-600 bg-amber-50 border-amber-100';
            case 'Due': return 'text-rose-600 bg-rose-50 border-rose-100';
            default: return 'text-gray-600 bg-gray-50 border-gray-100';
        }
    };

    return (
        <div className="space-y-8">
            {/* Student Summary Section */} {/* User Avatar / Initial / info */} {/* Decorative Background Elements */}
            {/* <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-2xl p-6 md:p-8 shadow-sm border border-blue-100/50 relative overflow-hidden">
               
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
 
               
                <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
                   
                    <div className="shrink-0">
                        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white text-4xl font-bold shadow-lg shadow-blue-200 rotate-3 transform transition-transform hover:rotate-0 duration-300">
                            {currentUser?.name?.charAt(0)}
                        </div>
                    </div>

                    <div className="flex-1 space-y-4">
                        <div>
                            <div className="flex flex-col md:flex-row items-center gap-3 mb-1">
                                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                                    {currentUser?.name}
                                </h2>
                                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider">
                                    Student
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium flex items-center justify-center md:justify-start gap-2">
                                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-mono">
                                    {stats.profile.index_number || 'NO INDEX'}
                                </span>
                                <span>•</span>
                                <span>Grade {stats.profile.grade || '-'}</span>
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                            <div className="bg-white/60 p-3 rounded-xl border border-blue-50">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Last Class</p>
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                    {stats.attendance.last_attended}
                                </p>
                            </div>
                            <div className="bg-white/60 p-3 rounded-xl border border-blue-50">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Classes Attended</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    {stats.attendance.total_classes}
                                </p>
                            </div>
                            <div className="bg-white/60 p-3 rounded-xl border border-blue-50">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Joined</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    {stats.profile.joined_date || '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Enrolled Courses */}
                <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-purple-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                                <FaBook size={20} />
                            </div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Academic</span>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-1">
                            {loading ? '-' : stats.enrolled_courses}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">Enrolled Courses</p>
                    </div>
                </div>

                {/* Average Grade */}
                <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                                <FaCheckCircle size={20} />
                            </div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Performance</span>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-1">
                            {loading ? '-' : stats.average_grade}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">Average Grade</p>
                    </div>
                </div>

                {/* Payment Status */}
                <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-24 h-24 bg-gray-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-4">
                            <div className={`p-3 rounded-xl ${getPaymentColor(stats.payment_status).split(' ')[1]} ${getPaymentColor(stats.payment_status).split(' ')[0]}`}>
                                <FaExclamationTriangle size={20} />
                            </div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Finance</span>
                        </div>
                        <h3 className={`text-3xl font-bold mb-1 ${getPaymentColor(stats.payment_status).split(' ')[0]}`}>
                            {loading ? '-' : stats.payment_status}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium">Payment Status</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

